// pages/organization/OrganizationPanel.jsx
import React, { useEffect, useState } from "react";
import Sidebar from "../../components/layout/Sidebar";
import Header from "../../components/layout/Header";
import { Helmet } from "react-helmet";
import OrgStats from "../../components/organization/OrgStats";
import OrgQuickLinks from "../../components/organization/OrgQuickLinks";
import OrgRecentActivity from "../../components/organization/OrgRecentActivity";
import OrgResultsPreview from "../../components/organization/OrgResultsPreview";
import { useTranslation } from "react-i18next"; // ← اضافه شده
import { BASE_URL } from "../../config";

// ---- helpers (permission-based) ----
function matchWithWildcard(perms, key) {
  if (perms.includes("*") || perms.includes("platform:*")) return true; // سوپراَدمین پلتفرم
  if (perms.includes(key)) return true; // تطابق دقیق

  const parts = key.split(":");
  for (let i = parts.length; i >= 1; i--) {
    const candidate = parts.slice(0, i).join(":") + ":*";
    if (perms.includes(candidate)) return true;
  }
  return false;
}

function has(perms, keys) {
  const list = Array.isArray(keys) ? keys : [keys];
  return list.every((k) => matchWithWildcard(perms, k));
}

function any(perms, keys) {
  const list = Array.isArray(keys) ? keys : [keys];
  return list.some((k) => matchWithWildcard(perms, k));
}

// نقشِ مجازی فقط برای سازگاری با کامپوننت‌هایی که هنوز role می‌گیرند
function deriveVirtualRole(perms) {
  const canUsersWrite = matchWithWildcard(perms, "org:users:write");
  const canCatsWrite = matchWithWildcard(perms, "org:categories:write");
  const canExamsWrite = matchWithWildcard(perms, "org:exams:write");
  const canResults = matchWithWildcard(perms, "org:results:read");
  const canGrading = any(perms, ["org:grading:read", "org:grading:write"]);
  const canExamsRead = matchWithWildcard(perms, "org:exams:read");

  // نزدیک‌ترین نگاشت به منطق قبلی:
  if (
    (canUsersWrite || perms.includes("org:*")) &&
    (canCatsWrite || perms.includes("org:*")) &&
    (canExamsWrite || perms.includes("org:*"))
  ) {
    return "org_admin";
  }
  if (canExamsRead && (canResults || canGrading)) {
    return "teacher";
  }
  return "editor";
}

const OrganizationPanel = () => {
  const { t, i18n } = useTranslation();
  const [user, setUser] = useState(null);
  const [statsData, setStatsData] = useState(null);

  useEffect(() => {
    const u = JSON.parse(localStorage.getItem("user"));
    setUser(u);

    const fetchStats = async () => {
      if (!u?.token) return;

      try {
        const res = await fetch(`${BASE_URL}/user/info/${u?._id}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": u.token,
          },
        });

        const json = await res.json();
        if (!json?.data) throw new Error(json?.message || "FAILED");

        setStatsData(json.data);
      } catch (e) {
        console.log(e);
      }
    };

    fetchStats();
  }, []);

  if (!user) return null;

  const perms = user?.perms || [];
  const isRtl = i18n.language.includes("fa");

  // کارت‌های آمار بر اساس permission
  const stats = [];

  // قبلاً برای org_admin, editor کارت‌های بیشتری بود؛ اینجا با مجوز می‌سازیم:
  if (any(perms, ["org:exams:read", "org:exams:write"])) {
    stats.push({
      title: t("org_panel.active_exams"),
      value: statsData?.examsCount ?? 0,
      icon: "pen",
      color: "purple",
    });
  }
  if (any(perms, ["org:users:read", "org:users:write"])) {
    stats.push({
      title: t("org_panel.org_members"),
      value: statsData?.membersCount ?? 0,
      icon: "user",
      color: "blue",
    });
  }
  if (matchWithWildcard(perms, "org:results:read")) {
    stats.push({
      title: t("org_panel.registered_results"),
      value: statsData?.resultsCount ?? 0,
      icon: "chart",
      color: "green",
    });
  }
  if (any(perms, ["org:categories:read", "org:categories:write"])) {
    stats.push({
      title: t("org_panel.categories"),
      value: statsData?.categoriesCount ?? 0,
      icon: "result",
      color: "red",
    });
  }

  // منطق قبلیِ teacher کارت‌های خاص داشت؛ نزدیک‌سازی با مجوز:

  const recentExams = [
    { title: t("org_panel.exam_title_1"), date: "1403/04/15" },
    { title: t("org_panel.exam_title_2"), date: "1403/04/12" },
    { title: t("org_panel.exam_title_3"), date: "1403/04/12" },
    { title: t("org_panel.exam_title_4"), date: "1403/04/10" },
  ];

  const mockResults = [
    {
      name: t("org_panel.user_1_name"),
      exam: t("org_panel.user_1_exam"),
      score: 82,
    },
    {
      name: t("org_panel.user_2_name"),
      exam: t("org_panel.user_2_exam"),
      score: 65,
    },
    {
      name: t("org_panel.user_3_name"),
      exam: t("org_panel.user_3_exam"),
      score: 91,
    },
  ];

  // کلاس‌های Tailwind
  const mainContentClass = `flex-1 flex flex-col px-2 pb-5 lg:px-0 lg:pb-0 ${
    isRtl ? "md:mr-64" : "md:ml-64"
  }`;
  const mainSectionClass = `flex-1 overflow-y-auto p-4 md:p-6 bg-gray-100 rounded-[16px] ${
    isRtl ? "lg:me-3 lg:mb-3" : "lg:ms-3 lg:mb-3"
  } space-y-6`;

  // نقشِ مجازی برای سازگاری با child components که prop role می‌خوان
  const virtualRole = deriveVirtualRole(perms);

  return (
    <div className="flex h-screen">
      <Helmet>
        <title>{t("org_panel.page_title")}</title>
      </Helmet>

      <Sidebar />
      <div className={mainContentClass}>
        <Header />
        <main className={mainSectionClass}>
          {stats.length > 0 && <OrgStats stats={stats} />}

          {/* قبلاً: فقط org_admin/editor → حالا: کسی که حداقل یکی از مجوزهای مدیریت رو دارد */}
          <OrgQuickLinks perms={perms} role={virtualRole} />

          {/* قبلاً: org_admin یا teacher → حالا: هرکس org:exams:read داشته باشد */}
          {matchWithWildcard(perms, "org:exams:read") && (
            <OrgRecentActivity
              exams={(statsData?.recentExams || []).map((e) => ({
                title: e?.title?.fa || e?.title?.en || "Exam",
                date: new Date(e.createdAt).toLocaleDateString("fa-IR"),
              }))}
            />
          )}

          {/* قبلاً: فقط org_admin → حالا: هرکس org:results:read داشته باشد */}
          {matchWithWildcard(perms, "org:results:read") && (
            <OrgResultsPreview
              results={(statsData?.recentResults || []).map((r) => ({
                name: r.userName,
                exam: r.examTitle,
                score: r.score,
              }))}
            />
          )}
        </main>
      </div>
    </div>
  );
};

export default OrganizationPanel;
